<script lang="ts">
	import { isLoading } from 'svelte-i18n';
	// attach the mobile functions to the window object on app start by importing this file
	import { mobile } from './services/mobile-rework';
	import { app, os } from 'src/stores/session/native-bridge';

	import { setUpI18n, setDictionary } from './services/i18n';
	import Router from 'svelte-spa-router';
	import type { RouteDetail } from 'svelte-spa-router';
	import { replace } from 'svelte-spa-router';
	import { wrap } from 'svelte-spa-router/wrap';
	import { user_settings } from 'src/stores/account/user';
	import { initializing_language } from 'src/stores/session/preferences';
	import { Theme, ToastContainer } from '@Shelly/shelly-design-system';
	import { glassBlurEnabled } from 'src/stores/features/dev/glass-blur';
	import { afterLoginParams } from 'src/stores/session/after-login-route';
	import { checkUser } from './helpers/user_helpers';
	import { attachIosDocumentScrollLock } from './helpers/ios_document_scroll_lock';
	import { onMount } from 'svelte';
	import { initSentry } from './services/sentry';
	import {
		getNativeInformation,
		initNative,
		initServerConnection,
		initWeb,
		prepareLocalStorageForOfflineMode,
		setNativeInformation,
	} from './services/init/app-initialization';
	import { setUnderSystemBarsEnabled } from 'src/stores/features/dev/under-status-bar';

	import LoginLayout from './routes/LoginLayout.svelte';
	import LoginLayoutV2Wrapper from './routes/LoginLayoutV2.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { get } from 'svelte/store';
	import PrivacyPolicy from './routes/LegalPages/PrivacyPolicy.svelte';
	import TermsAndConditions from './routes/LegalPages/TermsAndConditions.svelte';
	import DataAct from './routes/LegalPages/DataAct.svelte';
	import Changelog from './routes/Changelog.svelte';
	import MainLayoutManager from './routes/MainLayoutManager.svelte';
	import DevTrigger from './components/Dev/DevTrigger.svelte';
	import SafeAreaDebug from './components/Dev/SafeAreaDebug.svelte';

	const nativeInformation = getNativeInformation();
	const language = $user_settings.lang || $initializing_language || 'en';
	const PublicLayout = get(showNewLayout) ? LoginLayoutV2Wrapper : LoginLayout;
	const routes = {
		'/login': wrap({
			component: PublicLayout,
			conditions: [
				() => {
					return !checkUser();
				},
			],
		}),
		'/signup': PublicLayout,
		'/request-password-reset': PublicLayout,
		'/password-reset': PublicLayout,
		'/request-account-delete': PublicLayout,
		'/account-delete': PublicLayout,
		'/email-verification': PublicLayout,
		'/change-email': PublicLayout,
		'/privacy-policy': PrivacyPolicy,
		'/terms-and-conditions': TermsAndConditions,
		'/data-act': DataAct,
		'/changelog': Changelog,
		'/fast-login': PublicLayout,
		'/installer-login': PublicLayout,
		'/register_subaccount/:token/:email': PublicLayout,
		'/register_unit_transfer/*': PublicLayout,
		'/*': wrap({
			component: MainLayoutManager,
			conditions: [
				() => {
					return checkUser();
				},
			],
		}),
	};

	/**
	 * Check if there is a location in the detail object
	 * @param event
	 */
	const isRouteDetail = (e: CustomEvent<RouteDetail>): e is CustomEvent<RouteDetail> => {
		return e.detail && typeof e.detail === 'object' && e.detail?.location != '/';
	};

	const backToLogin = (e) => {
		if (e.detail.location.includes('login')) {
			console.log('[Login] Redirect home');
			replace('/');
			return;
		}

		if (isRouteDetail(e)) {
			console.log('[Login] Setting redirect URL after login:', e.detail);
			afterLoginParams.set(e.detail);
		}

		console.log('[Login] Redirecting to login');
		replace('/login');
	};

	const routeEvent = (e) => {
		console.warn('Caught event routeEvent', e.detail);
	};

	const isIOSDevice = () => {
		return (
			/iPad|iPhone|iPod/.test(navigator.userAgent) ||
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
		);
	};

	/**
	 * Handshake with the native shell, then draw the Android WebView under the
	 * system bars so CSS `env(safe-area-inset-*)` is available without a Dev
	 * Tools toggle. Must wait for `web_ready` — earlier `shelly://` calls are dropped.
	 */
	const bootstrapNativeContainer = async () => {
		try {
			await initNative($os);
		} finally {
			if ($os !== 'IOS') {
				setUnderSystemBarsEnabled(true, true);
			}
		}
	};

	onMount(() => {
		setDictionary();
		initSentry();
		setUpI18n(language);
		// Set the native information app, os, version
		setNativeInformation(nativeInformation);
		prepareLocalStorageForOfflineMode();

		if (!$app) {
			initWeb();
		} else {
			void bootstrapNativeContainer();
		}

		initServerConnection(nativeInformation.os);
		if (!isIOSDevice()) return;

		const root = document.documentElement;
		const aiModalClass = 'ai-scene-modal-open';
		let viewportListenersAttached = false;
		const detachDocumentScrollLock = attachIosDocumentScrollLock();

		const applyViewportHeight = () => {
			const viewportHeight = Math.round(window.visualViewport?.height ?? window.innerHeight);
			const viewportOffsetTop = Math.round(window.visualViewport?.offsetTop ?? 0);
			root.style.setProperty('--ios-visual-viewport-height', `${viewportHeight}px`);
			root.style.setProperty('--ios-visual-viewport-offset-top', `${viewportOffsetTop}px`);
		};

		const teardownAiViewportLock = () => {
			if (viewportListenersAttached) {
				window.visualViewport?.removeEventListener('resize', applyViewportHeight);
				window.visualViewport?.removeEventListener('scroll', applyViewportHeight);
				window.removeEventListener('resize', applyViewportHeight);
				viewportListenersAttached = false;
			}
			root.classList.remove('ios-vv-height-lock');
			root.style.removeProperty('--ios-visual-viewport-height');
			root.style.removeProperty('--ios-visual-viewport-offset-top');
		};

		const setupAiViewportLock = () => {
			root.classList.add('ios-vv-height-lock');
			applyViewportHeight();
			if (!viewportListenersAttached) {
				window.visualViewport?.addEventListener('resize', applyViewportHeight);
				window.visualViewport?.addEventListener('scroll', applyViewportHeight);
				window.addEventListener('resize', applyViewportHeight);
				viewportListenersAttached = true;
			}
		};

		const syncAiViewportLock = () => {
			if (document.body.classList.contains(aiModalClass)) {
				setupAiViewportLock();
				return;
			}
			teardownAiViewportLock();
		};

		const bodyClassObserver = new MutationObserver((records) => {
			for (const record of records) {
				if (record.type === 'attributes' && record.attributeName === 'class') {
					syncAiViewportLock();
					return;
				}
			}
		});

		syncAiViewportLock();
		bodyClassObserver.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		});

		return () => {
			bodyClassObserver.disconnect();
			teardownAiViewportLock();
			detachDocumentScrollLock();
		};
	});
</script>

<ToastContainer stacked />

<Theme glassBlur={$glassBlurEnabled}>
	{#if !$isLoading}
		<Router {routes} on:conditionsFailed={backToLogin} on:routeEvent={routeEvent} />
	{/if}
	<DevTrigger />
	<SafeAreaDebug />
</Theme>
